import { format, parseISO } from 'date-fns';
import { allPass, isNil, isNotNil, pick, pipe, prop, propOr, uniqBy } from 'ramda';

import ooeConstants from '../constants';
import { customerAccountTypeSchema, type CustomerAccountType } from '../dashboard/schemas';
import { Order, OrderDeliveryAddress } from '../order/schemas';
import normalizePhone from '../util/normalizePhone';
import { EmailSearchCustomer } from './schemas';

export type CopyableGuestField = 'first' | 'last' | 'email' | 'phone';

const ALL_COPYABLE_GUEST_FIELDS: CopyableGuestField[] = ['phone', 'email', 'first', 'last'];

/**
 * Determines which guest form fields should be offered when copying search text.
 * Empty or special characters → all fields; letters only → first/last/email;
 * digits only → phone/email.
 */
export const getCopyableFieldsForSearchValue = (value: string): CopyableGuestField[] => {
  const trimmed = value.trim();
  if (!trimmed) {
    return [];
  }

  const hasSpecialCharacters = /[^a-zA-Z0-9\s]/.test(trimmed);
  if (hasSpecialCharacters) {
    return ALL_COPYABLE_GUEST_FIELDS;
  }

  const hasLetter = /[a-zA-Z]/.test(trimmed);
  const hasDigit = /\d/.test(trimmed);

  if (hasDigit && !hasLetter) {
    return ['phone', 'email'];
  }

  if (hasLetter && !hasDigit) {
    return ['email', 'first', 'last'];
  }

  return ALL_COPYABLE_GUEST_FIELDS;
};

export const formatReadableDate = (dateTime: string) => {
  return format(parseISO(dateTime), ooeConstants.DATE_TIME_FORMAT.readableDate);
};

export const formatMemberOrderCardDate = (dateTime?: string) =>
  dateTime ? format(parseISO(dateTime), ooeConstants.DATE_TIME_FORMAT.memberOrderCardDate) : '';

export const validateGuest = (values: { first?: string; last?: string; email?: string; phone?: string }) => {
  const errors: {
    first?: string;
    last?: string;
    email?: string;
    phone?: string;
  } = {};
  if (!values.first) {
    errors.first = 'Required Field';
  } else if (values.first && !ooeConstants.NAME_REGEX.test(values.first)) {
    errors.first = 'Invalid entry. Only letters, hyphens and apostrophes';
  }
  if (!values.last) {
    errors.last = 'Required Field';
  } else if (values.last && !ooeConstants.NAME_REGEX.test(values.last)) {
    errors.last = 'Invalid entry. Only letters, hyphens and apostrophes';
  }
  if (!values.email) {
    errors.email = 'Required Field';
  } else if (values.email && !ooeConstants.EMAIL_REGEX.test(values.email)) {
    errors.email = 'Please enter a valid email address';
  }

  if (!values.phone) {
    errors.phone = 'Required Field';
  } else if (values.phone && !ooeConstants.PHONE_REGEX.test(values.phone)) {
    errors.phone = 'Please enter valid phone number';
  }
  return errors;
};

export const mapNameSearchResultToGuest = (result: Customer): GuestSearchResult => {
  const {
    name: { givenName, familyName },
    phoneNumbers,
    email,
    uid,
    loyaltyCardNumber,
  } = result;

  let phone: Nullable<string> | undefined = '';
  if (phoneNumbers && phoneNumbers[0]) {
    phone = normalizePhone(phoneNumbers[0].value);
  }

  return {
    cfaId: uid ?? '',
    firstName: givenName,
    lastName: familyName,
    phone,
    email,
    loyaltyCardNumber,
  };
};

export const mapNameSearchResultsToGuests = (results: Customer[]): GuestSearchResult[] => {
  return results.map(mapNameSearchResultToGuest);
};

export const isCustomerAccount = ({ accountType }: { accountType?: CustomerAccountType | string }) =>
  accountType === customerAccountTypeSchema.enum.MEMBER || accountType === 'ACCOUNT_TYPE_CUSTOMER';

export const mapEmailSearchResultToGuest = (result: EmailSearchCustomer): GuestSearchResult => {
  const { givenName, familyName, phoneNumber, emailAddress, id } = result;

  let phone: string | undefined = '';
  if (phoneNumber) {
    phone = normalizePhone(phoneNumber);
  }

  return {
    cfaId: id,
    firstName: givenName,
    lastName: familyName,
    phone,
    email: emailAddress,
  };
};

export const mapEmailSearchResultsToGuests = (results: EmailSearchCustomer[]): GuestSearchResult[] => {
  return results.filter(isCustomerAccount).map(mapEmailSearchResultToGuest);
};

export const mapPhoneSearchResultToGuest = (result: PhoneSearchCustomer): GuestSearchResult => {
  const { givenName, familyName, telephoneNumber, Mobile, primaryEmail, uid, loyaltyCardNumber } = result;
  const phone = normalizePhone(telephoneNumber ?? Mobile);

  return {
    cfaId: uid,
    firstName: givenName,
    lastName: familyName,
    phone,
    email: primaryEmail,
    loyaltyCardNumber,
  };
};

export const mapPhoneSearchResultsToGuests = (results: PhoneSearchCustomer[]): GuestSearchResult[] => {
  return results.map(mapPhoneSearchResultToGuest);
};

export const isAllowedCard = (card: Card) => {
  const cardType: string | undefined = propOr('', 'cardType', card);
  if (isNil(cardType)) {
    return false;
  }
  return ooeConstants.allowedCardTypes.includes(cardType.toUpperCase());
};

export const filterAllowedCards = (cards: Card[] = []) => cards.filter(isAllowedCard);

export const generateId = (card: Card) => {
  return `${card.expirationMonth}-${card.expirationYear}-${card.accountDisplay}-${card.cardType}`;
};

export const mapOrdersToPastOrders = (orders: Order[]) =>
  orders.map((order) => ({
    ...order,
    lineItems: order.lineItems?.map((item) => ({
      itemTag: item.itemTag,
      quantity: item.quantity,
      name: item.name,
      specialInstructions: item.specialInstructions,
      comboItems: item.comboItems,
      modifiers: item.modifiers,
    })),
  }));

export const mapOrdersToPastDeliveryAddresses = (
  orders: Order[],
  { includeCompanyName = false }: { includeCompanyName?: boolean } = {},
): OrderDeliveryAddress[] => {
  const displayedFields = ['addressLine1', 'city', 'state', 'zip'] as const;

  const hasAllRequiredFields = allPass(displayedFields.map((field) => pipe(prop(field), isNotNil)));

  const deliveryAddresses = orders
    .map(({ deliveryAddress, companyName }) =>
      includeCompanyName && deliveryAddress ? { ...deliveryAddress, companyName } : deliveryAddress,
    )
    .filter(isNotNil)
    .filter(hasAllRequiredFields);

  return uniqBy(pick(displayedFields), deliveryAddresses);
};
